import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import { createPortal } from 'react-dom';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '../../../../theme/useTheme';
import { AppDispatch, RootState } from '../../../../store';
import {
  ReportConfigRow,
  setReportConfigurationRows,
} from '../../../../store/slices/reportingConfigurationSlice';
import AdminBreadcrumb from '../../components/AdminBreadcrumb';
import AdminButton from '../../components/AdminButton';
import ReportingConfigurationFilterContent from './ReportingConfigurationFilterContent';
import ReportingConfigurationModal, { ReportingConfigurationFormState } from './ReportingConfigurationModal.tsx';
import { useToast } from '../../../../components/LhToastContainer';
import ReportingConfigurationTable from './ReportingConfigurationTable';
import reportingConfigurationApi, {
  ReportingConfigurationUpsertInput,
} from './reportingConfigurationApi';
import {
  ColumnFilters,
  FilterKey,
  FilterPopoverPosition,
  createReportingInitialColumnFilters,
  REPORTING_INITIAL_COLUMN_FILTERS,
  SortDirection,
  SortKey,
} from '../../constants/reportingConfiguration';
import styles from './ReportingConfiguration.styles';

interface ReportingConfigurationScreenProps {
  onBack: () => void;
}

const normalize = (value: string): string => value.trim().toLowerCase();

const ReportingConfigurationScreen: React.FC<ReportingConfigurationScreenProps> = ({ onBack }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const dispatch = useDispatch<AppDispatch>();
  const rows = useSelector((state: RootState) => state.reportingConfiguration.rows);
  const authUser = useSelector((state: RootState) => state.auth.user);
  const appUserInfo = useSelector((state: RootState) => state.appData.userInfo);
  const [searchValue, setSearchValue] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [activeFilterKey, setActiveFilterKey] = useState<FilterKey | null>(null);
  const [filterPopoverPosition, setFilterPopoverPosition] = useState<FilterPopoverPosition>({ top: 64, left: 16 });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [kpiInput, setKpiInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [statusToggleInProgressId, setStatusToggleInProgressId] = useState<string | null>(null);
  const toast = useToast();
  const [remoteKpiTags, setRemoteKpiTags] = useState<string[]>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>(createReportingInitialColumnFilters());
  const [formState, setFormState] = useState<ReportingConfigurationFormState>({
    id: null,
    reportId: null,
    name: '',
    primaryTag: 'Safety',
    secondaryTags: [],
    kpiTags: [],
    status: 'Active',
    description: '',
    link: '',
    icon: 'analytics',
    reportType: 'EXTERNAL',
  });
  const filterButtonRefs = useRef<Partial<Record<FilterKey, any>>>({});
  const filterPopoverRef = useRef<HTMLDivElement | null>(null);
  const { width: windowWidth } = useWindowDimensions();

  const auditContext = useMemo(() => {
    const userEmail = appUserInfo?.email ?? authUser?.email ?? authUser?.username ?? '';
    const gpid = String(appUserInfo?.GPID ?? authUser?.gpid ?? '');

    return { userEmail, gpid };
  }, [appUserInfo, authUser]);

  // Close filter popover on any scroll event (window-level or any scrollable ancestor)
  // but NOT when scrolling inside the filter popover itself
  useEffect(() => {
    if (Platform.OS !== 'web' || !activeFilterKey) return;

    const handleScroll = (event: Event) => {
      // If scroll is happening inside the filter popover, don't close it
      const target = event.target as HTMLElement | null;
      if (target && filterPopoverRef.current && filterPopoverRef.current.contains(target)) {
        return;
      }
      setActiveFilterKey(null);
    };

    // Listen on window for scroll events in capture phase to catch all scrollable containers
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [activeFilterKey]);

  useEffect(() => {
    let isMounted = true;

    const loadReportingConfiguration = async () => {
      try {
        const [apiRows, apiKpiTags] = await Promise.all([
          reportingConfigurationApi.getAll(),
          reportingConfigurationApi.getKpiTags().catch(() => []),
        ]);

        if (!isMounted) {
          return;
        }

        dispatch(setReportConfigurationRows(apiRows));

        setRemoteKpiTags(apiKpiTags);
        setLoading(false);
      } catch (error) {
        console.error('[ReportingConfigurationScreen] Failed to load report configurations:', error);
        if (isMounted) {
          setRemoteKpiTags([]);
          setLoading(false);
        }
      }
    };

    void loadReportingConfiguration();

    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  const kpiOptions = useMemo(() => {
    const merged = [
      ...remoteKpiTags,
      ...rows.flatMap((row) => row.kpiTags.map((tag) => tag.trim())),
    ].filter(Boolean);
    return Array.from(new Set(merged)).sort((a, b) => a.localeCompare(b));
  }, [remoteKpiTags, rows]);

  const activeCount = useMemo(() => rows.filter((row) => row.status === 'Active').length, [rows]);
  const disabledCount = useMemo(() => rows.filter((row) => row.status === 'Disabled').length, [rows]);
  const hasAppliedColumnFilters = useMemo(() => {
    return (
      !!columnFilters.nameQuery.trim() ||
      !!columnFilters.descriptionQuery.trim() ||
      !!columnFilters.linkQuery.trim() ||
      columnFilters.primaryTags.length > 0 ||
      columnFilters.secondaryTags.length > 0 ||
      columnFilters.kpiTags.length > 0 ||
      columnFilters.statuses.length > 0
    );
  }, [columnFilters]);

  const filteredRows = useMemo(() => {
    const query = normalize(searchValue);

    return rows.filter((row) => {
      if (columnFilters.nameQuery.trim() && !row.name.toLowerCase().includes(normalize(columnFilters.nameQuery))) {
        return false;
      }

      if (
        columnFilters.descriptionQuery.trim() &&
        !row.description.toLowerCase().includes(normalize(columnFilters.descriptionQuery))
      ) {
        return false;
      }

      if (columnFilters.linkQuery.trim() && !row.link.toLowerCase().includes(normalize(columnFilters.linkQuery))) {
        return false;
      }

      if (columnFilters.primaryTags.length > 0 && !columnFilters.primaryTags.includes(row.primaryTag)) {
        return false;
      }

      if (columnFilters.secondaryTags.length > 0) {
        const matchesSecondary = row.secondaryTags.some((tag) => columnFilters.secondaryTags.includes(tag));
        if (!matchesSecondary) {
          return false;
        }
      }

      if (columnFilters.kpiTags.length > 0) {
        const matchesKpi = row.kpiTags.some((tag) =>
          columnFilters.kpiTags.some((selected) => normalize(selected) === normalize(tag))
        );
        if (!matchesKpi) {
          return false;
        }
      }

      if (columnFilters.statuses.length > 0 && !columnFilters.statuses.includes(row.status)) {
        return false;
      }

      if (!query) {
        return true;
      }

      const searchable = [
        row.name,
        row.description,
        row.primaryTag,
        row.secondaryTags.join(' '),
        row.kpiTags.join(' '),
        row.link,
      ]
        .join(' ')
        .toLowerCase();

      return searchable.includes(query);
    });
  }, [columnFilters, rows, searchValue]);

  const uniqueSecondaryTags = useMemo(() => {
    return Array.from(new Set(rows.flatMap((row) => row.secondaryTags))).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const uniqueKpiTags = useMemo(() => {
    return Array.from(new Set(rows.flatMap((row) => row.kpiTags))).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const sortedRows = useMemo(() => {
    const data = [...filteredRows];

    const getSortValue = (row: ReportConfigRow): string => {
      switch (sortKey) {
        case 'name':
          return row.name;
        case 'primaryTag':
          return row.primaryTag;
        case 'secondaryTags':
          return row.secondaryTags.join(', ');
        case 'kpiTags':
          return row.kpiTags.join(', ');
        case 'status':
          return row.status;
        case 'description':
          return row.description;
        case 'link':
          return row.link;
        case 'icon':
          return row.icon;
        default:
          return row.name;
      }
    };

    data.sort((a, b) => {
      const left = getSortValue(a).toLowerCase();
      const right = getSortValue(b).toLowerCase();
      if (left === right) {
        return a.name.localeCompare(b.name);
      }
      return sortDirection === 'asc' ? left.localeCompare(right) : right.localeCompare(left);
    });

    return data;
  }, [filteredRows, sortDirection, sortKey]);

  const openAddModal = () => {
    setFormState({
      id: null,
      reportId: null,
      name: '',
      primaryTag: 'Safety',
      secondaryTags: [],
      kpiTags: [],
      status: 'Active',
      description: '',
      link: '',
      icon: 'analytics',
      reportType: 'EXTERNAL',
    });
    setKpiInput('');
    setIsModalVisible(true);
  };

  const openEditModal = (row: ReportConfigRow) => {
    setFormState({ ...row });
    setKpiInput('');
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setKpiInput('');
  };

  const onSave = () => {
    if (!formState.name.trim() || !formState.description.trim() || !formState.link.trim()) {
      return;
    }

    if (!auditContext.userEmail || !auditContext.gpid) {
      console.error('[ReportingConfigurationScreen] Missing user email or GPID for report save.');
      return;
    }

    const payload: ReportingConfigurationUpsertInput = {
      reportId: formState.reportId,
      name: formState.name.trim(),
      primaryTag: formState.primaryTag,
      secondaryTags: formState.secondaryTags,
      kpiTags: formState.kpiTags,
      status: formState.status,
      description: formState.description.trim(),
      link: formState.link.trim(),
      icon: formState.icon,
    };

    const applySavedRow = (savedRow: ReportConfigRow) => {
      const nextRows = [...rows];

      if (savedRow.reportId != null) {
        const matchIndex = nextRows.findIndex((row) => row.reportId === savedRow.reportId || row.id === savedRow.id);
        if (matchIndex >= 0) {
          nextRows[matchIndex] = savedRow;
        } else {
          nextRows.push(savedRow);
        }
      } else {
        const matchIndex = nextRows.findIndex((row) => row.id === formState.id);
        if (matchIndex >= 0) {
          nextRows[matchIndex] = savedRow;
        } else {
          nextRows.push(savedRow);
        }
      }

      dispatch(setReportConfigurationRows(nextRows));
    };

    const saveReportConfiguration = async () => {
      setIsSaving(true);
      try {
        const savedRow = formState.reportId != null
          ? await reportingConfigurationApi.update(formState.reportId, payload, auditContext)
          : await reportingConfigurationApi.create(payload, auditContext);

        applySavedRow(savedRow);
        toast.show('Report saved', 'success');
        closeModal();
      } catch (error: any) {
        console.error('[ReportingConfigurationScreen] Failed to save report configuration:', error);
        const message = error?.message ?? 'Failed to save report configuration';
        try {
          toast.show(message, 'error');
        } catch {
          // ignore toast failures
        }
      } finally {
        setIsSaving(false);
      }
    };

    void saveReportConfiguration();
  };

  const onToggleStatus = (row: ReportConfigRow) => {
    if (!auditContext.userEmail || !auditContext.gpid) {
      console.error('[ReportingConfigurationScreen] Missing user email or GPID for report status update.');
      return;
    }

    if (statusToggleInProgressId === row.id) {
      return;
    }

    const nextStatus: ReportConfigRow['status'] = row.status === 'Active' ? 'Disabled' : 'Active';

    const applyUpdatedStatusRow = (updatedRow: ReportConfigRow) => {
      const nextRows = rows.map((current) => {
        if (updatedRow.reportId != null && current.reportId != null) {
          return current.reportId === updatedRow.reportId ? updatedRow : current;
        }
        return current.id === row.id ? updatedRow : current;
      });

      dispatch(setReportConfigurationRows(nextRows));
    };

    const toggleStatus = async () => {
      setStatusToggleInProgressId(row.id);
      try {
        let updatedRow: ReportConfigRow;

        if (row.reportId == null) {
          throw new Error('Unable to update status: missing report id.');
        }

        updatedRow = await reportingConfigurationApi.updateStatus({
          reportId: row.reportId,
          status: nextStatus,
          ...auditContext,
        });

        applyUpdatedStatusRow(updatedRow);
        toast.show(`Report ${nextStatus === 'Active' ? 'activated' : 'disabled'} successfully.`, 'success');
      } catch (error: any) {
        console.error('[ReportingConfigurationScreen] Failed to update report status:', error);
        const message = error?.message ?? 'Failed to update report status';
        try {
          toast.show(message, 'error');
        } catch {
          // ignore toast failures
        }
      } finally {
        setStatusToggleInProgressId(null);
      }
    };

    void toggleStatus();
  };

  const toggleSecondaryTag = (tag: ReportingConfigurationFormState['primaryTag']) => {
    setFormState((current) => {
      const exists = current.secondaryTags.includes(tag);
      return {
        ...current,
        secondaryTags: exists ? current.secondaryTags.filter((value) => value !== tag) : [...current.secondaryTags, tag],
      };
    });
  };

  const addKpiTag = (tag: string) => {
    const clean = tag.trim();
    if (!clean) {
      return;
    }

    setFormState((current) => {
      if (current.kpiTags.some((value) => normalize(value) === normalize(clean))) {
        return current;
      }
      return { ...current, kpiTags: [...current.kpiTags, clean] };
    });
    setKpiInput('');
  };

  const removeKpiTag = (tag: string) => {
    setFormState((current) => ({
      ...current,
      kpiTags: current.kpiTags.filter((value) => value !== tag),
    }));
  };

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortKey(key);
    setSortDirection('asc');
  };

  const setTextColumnFilter = (key: 'nameQuery' | 'descriptionQuery' | 'linkQuery', value: string) => {
    setColumnFilters((current) => ({ ...current, [key]: value }));
  };

  const toggleListFilter = <T extends string>(
    key: 'primaryTags' | 'secondaryTags' | 'kpiTags' | 'statuses',
    value: T
  ) => {
    setColumnFilters((current) => {
      const currentValue = current[key] as T[];
      const exists = currentValue.includes(value);
      return {
        ...current,
        [key]: exists ? currentValue.filter((item) => item !== value) : [...currentValue, value],
      };
    });
  };

  const clearSingleFilter = (key: FilterKey) => {
    setColumnFilters((current) => {
      if (key === 'name') return { ...current, nameQuery: '' };
      if (key === 'description') return { ...current, descriptionQuery: '' };
      if (key === 'link') return { ...current, linkQuery: '' };
      if (key === 'primaryTag') return { ...current, primaryTags: [] };
      if (key === 'secondaryTags') return { ...current, secondaryTags: [] };
      if (key === 'kpiTags') return { ...current, kpiTags: [] };
      if (key === 'status') return { ...current, statuses: [] };
      return current;
    });
  };

  const clearAllFilters = () => {
    setColumnFilters(createReportingInitialColumnFilters());
    setSearchValue('');
    setActiveFilterKey(null);
  };

  const isFilterActive = (key: FilterKey): boolean => {
    if (key === 'name') return !!columnFilters.nameQuery.trim();
    if (key === 'description') return !!columnFilters.descriptionQuery.trim();
    if (key === 'link') return !!columnFilters.linkQuery.trim();
    if (key === 'primaryTag') return columnFilters.primaryTags.length > 0;
    if (key === 'secondaryTags') return columnFilters.secondaryTags.length > 0;
    if (key === 'kpiTags') return columnFilters.kpiTags.length > 0;
    return columnFilters.statuses.length > 0;
  };

  const openFilterPopover = (key: FilterKey) => {
    const ref = filterButtonRefs.current[key];
    const desiredWidth = 220;

    if (!ref || typeof ref.measureInWindow !== 'function') {
      setFilterPopoverPosition({ top: 64, left: Math.max(8, windowWidth - desiredWidth - 8) });
      setActiveFilterKey(key);
      return;
    }

    ref.measureInWindow((x: number, y: number, measuredWidth: number, measuredHeight: number) => {
      const unclampedLeft = x + measuredWidth - desiredWidth;
      const left = Math.max(8, Math.min(unclampedLeft, windowWidth - desiredWidth - 8));
      const top = y + measuredHeight + 6;
      setFilterPopoverPosition({ top, left });
      setActiveFilterKey(key);
    });
  };

  const renderSortIcon = (key: SortKey) => {
    const isActive = key === sortKey;
    const iconName = isActive && sortDirection === 'desc' ? 'arrow-downward' : 'arrow-upward';
    const activeColor = isDark ? colors.brand.primary : '#005CBC';
    const inactiveColor = isDark ? colors.text.disabled : '#838383';
    return <MaterialIcons name={iconName} size={14} color={isActive ? activeColor : inactiveColor} />;
  };

  const renderBadge = (label: string, variant: 'green' | 'gray' | 'blue', inTableDarkMode?: boolean) => {
    // In dark mode (both table rows and non-table contexts) use bright text so badges
    // are legible against the dark elevated row background (#2B2B2B).
    // Light theme uses the original palette unchanged.
    const palette = isDark
      ? variant === 'green'
        ? { bg: 'rgba(56,182,56,0.22)', text: '#8FEBA8' }
        : variant === 'blue'
          ? { bg: 'rgba(61,139,255,0.22)', text: '#99CFFF' }
          : { bg: 'rgba(176,176,176,0.18)', text: '#D4D4D4' }
      : variant === 'green'
        ? { bg: '#CAF3C6', text: '#0C420B' }
        : variant === 'blue'
          ? { bg: '#DAEEFA', text: '#01346F' }
          : { bg: '#E6E6E6', text: '#4B4B4B' };

    return (
      <View style={[styles.badge, { backgroundColor: palette.bg }]}>
        <Text style={[styles.badgeText, { color: palette.text }]}>{label}</Text>
      </View>
    );
  };

  const renderFilterOverlay = () => {
    if (!activeFilterKey) return null;

    const popoverContent = (
      <Pressable
        style={fixedOverlayStyles.backdrop}
        onPress={() => setActiveFilterKey(null)}
      >
        <Pressable
          style={[
            styles.filterPopover,
            fixedOverlayStyles.popover,
            {
              top: filterPopoverPosition.top,
              left: filterPopoverPosition.left,
            },
            isDark && {
              backgroundColor: colors.background.elevated,
              borderColor: colors.border.default,
            },
          ]}
          onPress={(event) => event.stopPropagation()}
        >
          <ReportingConfigurationFilterContent
            filterKey={activeFilterKey}
            columnFilters={columnFilters}
            uniqueSecondaryTags={uniqueSecondaryTags}
            uniqueKpiTags={uniqueKpiTags}
            setTextColumnFilter={setTextColumnFilter}
            toggleListFilter={toggleListFilter}
            clearSingleFilter={clearSingleFilter}
            onClose={() => setActiveFilterKey(null)}
          />
        </Pressable>
      </Pressable>
    );

    // On web, use createPortal to render at document.body level so it's not affected by scroll
    // Wrap in a div with ref so we can detect scroll events inside the popover
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      return createPortal(
        <div ref={filterPopoverRef} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999 }}>
          {popoverContent}
        </div>,
        document.body
      );
    }

    return popoverContent;
  };

  return (
    <View style={{ flex: 1 }}>
      <ScrollView
        style={[styles.container, isDark && { backgroundColor: colors.background.page }]}
        contentContainerStyle={styles.content}
        onScroll={() => { if (activeFilterKey) setActiveFilterKey(null); }}
        scrollEventThrottle={16}
      >
        <AdminBreadcrumb
          items={[
            { label: 'Configuration', onPress: onBack },
            { label: 'Reporting Configuration' },
          ]}
        />

        <View style={styles.headerRow}>
          <View>
            <Text style={[styles.pageTitle, isDark && { color: colors.text.primary }]}>Reporting Configuration</Text>
            <Text style={[styles.pageSubtitle, isDark && { color: colors.text.secondary }]}>Manage report metadata, tags, KPI assignments, and visibility.</Text>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            <AdminButton label="Clear Filters" variant="secondary" onPress={clearAllFilters} disabled={!hasAppliedColumnFilters} />
            <AdminButton label="+ Add Report" variant="primary" onPress={openAddModal} />
          </View>
        </View>

        <View style={[styles.infoBanner, isDark && { backgroundColor: colors.statusBackground.info, borderColor: colors.border.default }]}>
          <MaterialIcons name="info-outline" size={16} color={isDark ? colors.brand.primary : '#005CBC'} />
          <Text style={[styles.infoText, isDark && { color: colors.text.secondary }]}>
            This table controls how reports are tagged, organized, and displayed in the Reports tab of the Analytics Hub.
          </Text>
        </View>

        <ReportingConfigurationTable
          rows={rows}
          sortedRows={sortedRows}
          activeCount={activeCount}
          disabledCount={disabledCount}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          hasAppliedColumnFilters={hasAppliedColumnFilters}
          clearAllFilters={clearAllFilters}
          toggleSort={toggleSort}
          renderSortIcon={renderSortIcon}
          openEditModal={openEditModal}
          renderBadge={renderBadge}
          activeFilterKey={activeFilterKey}
          setActiveFilterKey={setActiveFilterKey}
          openFilterPopover={openFilterPopover}
          isFilterActive={isFilterActive}
          filterButtonRefs={filterButtonRefs}
          onToggleStatus={onToggleStatus}
          statusToggleInProgressId={statusToggleInProgressId}
          loading={loading}
        />

      <ReportingConfigurationModal
        visible={isModalVisible}
        formState={formState}
        kpiInput={kpiInput}
        kpiOptions={kpiOptions}
        onSetName={(value) => setFormState((current) => ({ ...current, name: value }))}
        onSetDescription={(value) => setFormState((current) => ({ ...current, description: value }))}
        onSetLink={(value) => setFormState((current) => ({ ...current, link: value }))}
        onSetIcon={(value) => setFormState((current) => ({ ...current, icon: value }))}
        onSetPrimaryTag={(value) => setFormState((current) => ({ ...current, primaryTag: value }))}
        onToggleStatus={() =>
          setFormState((current) => ({
            ...current,
            status: current.status === 'Active' ? 'Disabled' : 'Active',
          }))
        }
        onToggleSecondaryTag={toggleSecondaryTag}
        onSetKpiInput={setKpiInput}
        onAddKpiTag={addKpiTag}
        onRemoveKpiTag={removeKpiTag}
        onClose={closeModal}
        onSave={onSave}
        isSaving={isSaving}
      />
      </ScrollView>

      {renderFilterOverlay()}
    </View>
  );
};

const fixedOverlayStyles = StyleSheet.create({
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
    zIndex: 9999,
    ...(Platform.OS === 'web' ? { position: 'fixed' as any } : {}),
  },
  popover: {
    position: 'absolute',
    ...(Platform.OS === 'web' ? { position: 'fixed' as any } : {}),
  },
});

export default ReportingConfigurationScreen;
